import { z } from 'zod';
import { BoardData, BuiltinLayerKey, InventoryEntry } from '../model/schema.js';
import { BoardFileMime } from '../export/portable.js';
import { AiExtraction, AiImportKind, AiMaterialExtraction } from '../aiimport/schema.js';
import { TemplateData } from '../templates/template.js';

/**
 * REST contract shared by server (request/response validation + OpenAPI generation) and web
 * (typed client + runtime response validation). Timestamps are ISO-8601 strings.
 */

const id = z.string().min(1);
const isoDate = z.string().datetime({ offset: true });
/**
 * Pragmatic e-mail check: local@domain without whitespace. Deliberately accepts TLD-less
 * domains such as `admin@localhost` (dev identity provider users).
 */
export const Email = z
  .string()
  .max(254)
  .regex(/^[^\s@]+@[^\s@]+$/, 'Invalid email');

export const WorkspaceRoleSchema = z.enum(['owner', 'admin', 'editor', 'viewer']);
export const LinkAccessSchema = z.enum(['view', 'comment', 'edit']);
export const LinkStateSchema = z.enum(['active', 'expired', 'revoked']);

export const ErrorResponse = z.object({
  error: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponse>;

export const RuntimeConfig = z.object({
  version: z.string(),
  authEnabled: z.boolean(),
  collabUrl: z.string(),
  defaultLocale: z.enum(['en', 'de']),
  errorTrackingDsn: z.string().nullable(),
  limits: z.object({
    maxUploadBytes: z.number().int(),
  }),
  /** AI rider import (U9); absent on older servers = disabled. */
  aiImport: z
    .object({
      enabled: z.boolean(),
      model: z.string().nullable(),
      maxPages: z.number().int(),
    })
    .optional(),
});
export type RuntimeConfig = z.infer<typeof RuntimeConfig>;

/** A plan from theta-portal (U78), see core `Plan`. */
export const PlanDto = z.object({
  product: z.string(),
  tier: z.string(),
  tierName: z.string(),
  status: z.string(),
  features: z.array(z.string()),
  periodEnd: z.string().optional(),
});

/** The UI language of a member, used for their e-mails (U87). */
export const UpdateMe = z.object({ locale: z.enum(['en', 'de']) });
export type UpdateMe = z.infer<typeof UpdateMe>;

export const Me = z.object({
  id,
  email: z.string(),
  name: z.string(),
  locale: z.enum(['en', 'de']),
  platformAdmin: z.boolean(),
  workspaces: z.array(z.object({ id, name: z.string(), role: WorkspaceRoleSchema })),
  /** Sign-in provider and, for theta-portal, its URL (account, plans) – U78. */
  account: z
    .object({ provider: z.enum(['oidc', 'theta']), centralUrl: z.string().optional() })
    .optional(),
  /** The user's plan from theta-portal (U78); null = none, missing = provider without plans. */
  plan: PlanDto.nullable().optional(),
});
export type Me = z.infer<typeof Me>;

export const Workspace = z.object({
  id,
  name: z.string(),
  plan: z.string(),
  role: WorkspaceRoleSchema,
  createdAt: isoDate,
  usage: z.object({
    boards: z.number().int(),
    activeLinks: z.number().int(),
    storageBytes: z.number().int(),
  }),
  limits: z.object({
    boards: z.number().int(),
    activeLinks: z.number().int(),
    storageBytes: z.number().int(),
    guestsPerLink: z.number().int(),
  }),
});
export type Workspace = z.infer<typeof Workspace>;

export const CreateWorkspace = z.object({ name: z.string().min(1).max(100) });
export const UpdateWorkspace = z.object({ name: z.string().min(1).max(100) });

export const Member = z.object({
  userId: id,
  email: z.string(),
  name: z.string(),
  role: WorkspaceRoleSchema,
});
export type Member = z.infer<typeof Member>;
export const AddMember = z.object({ email: Email, role: WorkspaceRoleSchema });
export const UpdateMember = z.object({ role: WorkspaceRoleSchema });

export const BoardSummary = z.object({
  id,
  workspaceId: id,
  name: z.string(),
  createdAt: isoDate,
  updatedAt: isoDate,
  deletedAt: isoDate.nullable(),
  /** Member who created the board – its owner for board-owner actions (U93); null once deleted. */
  createdBy: id.nullable().optional(),
});
export type BoardSummary = z.infer<typeof BoardSummary>;
export const CreateBoard = z.object({
  name: z.string().min(1).max(200),
  stageWidth: z.number().positive().max(200).optional(),
  stageDepth: z.number().positive().max(200).optional(),
  /** `demo` seeds a complete example band (see core/demo). */
  template: z.enum(['blank', 'demo']).optional(),
  /** Workspace template inserted at the stage centre (decision U16); wins over `template`. */
  templateId: id.optional(),
});
export const UpdateBoard = z.object({ name: z.string().min(1).max(200) });
export const CloneBoard = z.object({ name: z.string().min(1).max(200).optional() });

export const DocumentResponse = z.object({ data: BoardData, version: z.number().int() });
export type DocumentResponse = z.infer<typeof DocumentResponse>;

export const LeaseHolder = z.object({
  kind: z.enum(['member', 'guest']),
  id,
  name: z.string(),
});
export const LeaseRequest = z.object({ takeover: z.boolean().optional() });
export const LeaseResponse = z.object({
  granted: z.boolean(),
  holder: LeaseHolder.nullable(),
  expiresAt: isoDate.nullable(),
  ttlSeconds: z.number().int(),
});
export type LeaseResponse = z.infer<typeof LeaseResponse>;

/** Web push (U52): the server's VAPID key (null = push not configured) and a device subscription. */
export const PushConfig = z.object({ publicKey: z.string().nullable() });
export const PushSubscriptionBody = z.object({
  endpoint: z.string().url().max(2000),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
export type PushSubscriptionBody = z.infer<typeof PushSubscriptionBody>;
export const PushUnsubscribeBody = z.object({ endpoint: z.string().url().max(2000) });

/** Search across the boards of a workspace (U51). */
export const SearchResult = z.object({
  board: BoardSummary,
  hits: z.array(
    z.object({
      kind: z.enum(['board', 'item', 'channel', 'mix', 'note', 'rider']),
      id: z.string(),
      text: z.string(),
    }),
  ),
});
export type SearchResult = z.infer<typeof SearchResult>;

/** Change history entry (U50): who changed what when. */
export const HistoryAuthor = z.object({
  kind: z.enum(['member', 'guest']),
  id,
  name: z.string(),
});
export const HistoryChange = z.object({
  collection: z.string(),
  id: z.string(),
  kind: z.enum(['added', 'removed', 'changed']),
  label: z.string(),
  fields: z.array(z.string()),
});
export const HistoryEntry = z.object({
  id,
  at: isoDate,
  authors: z.array(HistoryAuthor),
  changes: z.array(HistoryChange),
  /** Changes beyond the stored ones. */
  more: z.number().int().nonnegative(),
});
export type HistoryEntry = z.infer<typeof HistoryEntry>;
export const HistoryPage = z.object({
  items: z.array(HistoryEntry),
  /** Entry id to pass as `before` for the next (older) page; null at the end. */
  next: z.string().nullable(),
});
export type HistoryPage = z.infer<typeof HistoryPage>;

/** A sign-off under a released version (U80); the drawn signature is fetched on its own. */
export const Signoff = z.object({
  id,
  name: z.string(),
  role: z.string(),
  comment: z.string().nullable(),
  at: isoDate,
  hasSignature: z.boolean(),
});
export type Signoff = z.infer<typeof Signoff>;
export const Release = z.object({ number: z.number().int(), at: isoDate, by: z.string() });
export type Release = z.infer<typeof Release>;

export const Snapshot = z.object({
  id,
  boardId: id,
  name: z.string(),
  version: z.number().int(),
  createdAt: isoDate,
  createdBy: z.string(),
  pdfUrl: z.string().nullable(),
  /** Released version (U80): numbered, locked, signed off below. */
  release: Release.nullable(),
  signoffs: z.array(Signoff),
});
export type Snapshot = z.infer<typeof Snapshot>;
/** PNG data URL of a drawn signature – no pen dynamics (U80). */
export const SIGNATURE_PREFIX = 'data:image/png;base64,';
export const CreateSignoff = z.object({
  name: z.string().trim().min(1).max(120),
  role: z.string().trim().max(60),
  comment: z.string().trim().max(500).optional(),
  signature: z.string().startsWith(SIGNATURE_PREFIX).max(400_000).optional(),
});
export const ReleaseSettings = z.object({ requireSignature: z.boolean() });
/** A release as a guest sees it (OIL-163): its number, name and sign-offs. */
export const GuestRelease = z.object({
  id,
  number: z.number().int(),
  name: z.string(),
  at: isoDate,
  signoffs: z.array(Signoff),
});
export type GuestRelease = z.infer<typeof GuestRelease>;
export const GuestReleases = z.object({
  requireSignature: z.boolean(),
  releases: z.array(GuestRelease),
});
export type GuestReleases = z.infer<typeof GuestReleases>;
/** The release's board in the guest's projection (layers of the link). */
export const GuestReleaseDetail = GuestRelease.extend({ data: BoardData });
export const CreateSnapshot = z.object({ name: z.string().min(1).max(200) });
export const SnapshotDetail = Snapshot.extend({ data: BoardData });

export const Link = z.object({
  id,
  boardId: id,
  name: z.string(),
  access: LinkAccessSchema,
  layerKeys: z.array(BuiltinLayerKey).nullable(),
  expiresAt: isoDate,
  revokedAt: isoDate.nullable(),
  state: LinkStateSchema,
  createdAt: isoDate,
  guestCount: z.number().int(),
  /** Share URL – returned to the board's admins (derivable token); absent for legacy links. */
  url: z.string().optional(),
});
export type Link = z.infer<typeof Link>;
export const CreateLink = z.object({
  name: z.string().min(1).max(200),
  access: LinkAccessSchema,
  layerKeys: z.array(BuiltinLayerKey).nullable().optional(),
  expiresAt: isoDate.optional(),
});
export const UpdateLink = z.object({
  name: z.string().min(1).max(200).optional(),
  /** `rotate` issues a new URL (the old one stops working). */
  action: z.enum(['revoke', 'extend', 'rotate']).optional(),
  expiresAt: isoDate.optional(),
});

/** Guest-note overlay of a comment/edit link, visible to every board reader (link management stays admin-only). */
export const OverlayInfo = z.object({
  linkId: id,
  name: z.string(),
  access: LinkAccessSchema,
  state: LinkStateSchema,
});
export type OverlayInfo = z.infer<typeof OverlayInfo>;

/** Someone a guest may @mention: workspace members and guests of the same link. */
export const MentionCandidate = z.object({
  kind: z.enum(['member', 'guest']),
  id,
  name: z.string(),
});
export type MentionCandidate = z.infer<typeof MentionCandidate>;

/** What a guest sees about a link. Never contains the link title (decision U5: members only). */
export const ShareInfo = z.object({
  boardName: z.string(),
  access: LinkAccessSchema,
  state: LinkStateSchema,
  expiresAt: isoDate,
  layerKeys: z.array(BuiltinLayerKey).nullable(),
  guest: z.object({ id, displayName: z.string() }).nullable(),
  boardId: id,
  linkId: id,
});
export type ShareInfo = z.infer<typeof ShareInfo>;
/** "I am new": registers a new guest person on the link (decision C1, revised). */
export const NewGuestPerson = z.object({
  displayName: z.string().min(1).max(80),
  email: Email.optional(),
  emailConsent: z.boolean(),
});
/** "I am …": a new device session for a person already registered on this link. */
export const ExistingGuestPerson = z.object({ personId: id });
export const CreateGuestSession = z.union([ExistingGuestPerson, NewGuestPerson]);
export type CreateGuestSession = z.infer<typeof CreateGuestSession>;

/** A guest person of one link (never persons of other links). The guest id is the person id. */
export const GuestPerson = z.object({ id, displayName: z.string() });
export type GuestPerson = z.infer<typeof GuestPerson>;

/**
 * One visit of a person (member or guest person) on a board (decision C1, revised). The board
 * state at visit start is fetched separately (`GET /visits/:vid/state`). `caughtUp` marks the
 * synthetic visit stored by "mark as caught up".
 */
export const Visit = z.object({
  id,
  boardId: id,
  startedAt: isoDate,
  endedAt: isoDate.nullable(),
  caughtUp: z.boolean(),
});
export type Visit = z.infer<typeof Visit>;
export const VisitState = z.object({ visit: Visit, data: BoardData });
export type VisitState = z.infer<typeof VisitState>;
export const ExtensionRequest = z.object({ message: z.string().max(1000).optional() });

/** Table views whose rows can carry comments (DataGrid ids in the web app). */
export const COMMENT_TABLES = [
  'input',
  'patch',
  'monitors',
  'mixes',
  'sends',
  'power',
  'power-sources',
  'power-loads',
  'network',
  'stands',
  'stand-summary',
  'openitems',
] as const;
export const CommentTable = z.enum(COMMENT_TABLES);

export const CommentAnchor = z.discriminatedUnion('type', [
  z.object({ type: z.literal('item'), itemId: id }),
  z.object({ type: z.literal('point'), x: z.number(), y: z.number() }),
  z.object({
    type: z.literal('row'),
    table: CommentTable,
    rowKey: z.string(),
    column: z.string().optional(),
  }),
  z.object({ type: z.literal('board') }),
]);
export type CommentAnchor = z.infer<typeof CommentAnchor>;

export const Comment = z.object({
  id,
  boardId: id,
  linkId: z.string().nullable(),
  parentId: z.string().nullable(),
  author: z.object({
    kind: z.enum(['member', 'guest']),
    id,
    name: z.string(),
    viaLink: z.string().nullable(),
  }),
  body: z.string(),
  anchor: CommentAnchor,
  version: z.number().int(),
  resolvedAt: isoDate.nullable(),
  editedAt: isoDate.nullable(),
  createdAt: isoDate,
  attachments: z.array(z.object({ id, name: z.string(), mime: z.string(), url: z.string() })),
  mentions: z.array(z.string()),
});
export type Comment = z.infer<typeof Comment>;
export const CreateComment = z.object({
  body: z.string().min(1).max(10_000),
  anchor: CommentAnchor,
  parentId: z.string().optional(),
  attachmentIds: z.array(id).max(10).optional(),
});
export const UpdateComment = z.object({ body: z.string().min(1).max(10_000) });

export const UploadRequest = z.object({
  name: z.string().min(1).max(255),
  mime: BoardFileMime,
  size: z.number().int().positive(),
});
export const UploadResponse = z.object({
  fileId: id,
  uploadUrl: z.string(),
  method: z.literal('PUT'),
});

/** Open items (decisions O1–O6). Topics are the built-in object layers plus `general`. */
export const OPEN_ITEM_TOPICS = [
  'stage',
  'musicians',
  'backline',
  'microphones',
  'monitors',
  'pa',
  'stageboxes',
  'cabling',
  'power',
  'network',
  'lighting',
  'general',
] as const;
export const OpenItemTopic = z.enum(OPEN_ITEM_TOPICS);
export type OpenItemTopic = z.infer<typeof OpenItemTopic>;
export const OPEN_ITEM_STATUSES = ['open', 'in_progress', 'waiting', 'resolved'] as const;
export const OpenItemStatus = z.enum(OPEN_ITEM_STATUSES);
export type OpenItemStatus = z.infer<typeof OpenItemStatus>;
/** Calendar date `YYYY-MM-DD` (due dates have no time zone). */
export const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

/**
 * A member or a guest of the item's scope (originator, assignee, reply author, history actor).
 * Never carries a link title (decision U5).
 */
export const OpenItemPerson = z.object({
  kind: z.enum(['member', 'guest']),
  id,
  name: z.string(),
});
export type OpenItemPerson = z.infer<typeof OpenItemPerson>;
/** Reference to an assignee in requests (`null` = unassigned). */
export const PersonRef = z.object({ kind: z.enum(['member', 'guest']), id });
export type PersonRef = z.infer<typeof PersonRef>;

export const OPEN_ITEM_EVENT_TYPES = [
  'created',
  'title',
  'description',
  'status',
  'reopened',
  'assignee',
  'topic',
  'due',
  'anchor',
] as const;
export const OpenItemEventType = z.enum(OPEN_ITEM_EVENT_TYPES);
export type OpenItemEventType = z.infer<typeof OpenItemEventType>;
/** History entry. `from`/`to` are display values (status/topic keys, assignee names, dates). */
export const OpenItemEvent = z.object({
  id,
  type: OpenItemEventType,
  actor: OpenItemPerson,
  from: z.string().nullable(),
  to: z.string().nullable(),
  createdAt: isoDate,
});
export type OpenItemEvent = z.infer<typeof OpenItemEvent>;

export const OpenItemReply = z.object({
  id,
  author: OpenItemPerson,
  body: z.string(),
  mentions: z.array(z.string()),
  createdAt: isoDate,
});
export type OpenItemReply = z.infer<typeof OpenItemReply>;

export const OpenItem = z.object({
  id,
  boardId: id,
  /** Scope (decision O1): `null` = internal (members only), else the share link. */
  linkId: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  status: OpenItemStatus,
  topic: OpenItemTopic,
  /** Same anchors as comments, or `null` for items entered directly into the list. */
  anchor: CommentAnchor.nullable(),
  assignee: OpenItemPerson.nullable(),
  originator: OpenItemPerson,
  dueDate: DateOnly.nullable(),
  /** The comment this item was created from ("Comment → open item"). */
  sourceCommentId: z.string().nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
  resolvedAt: isoDate.nullable(),
  history: z.array(OpenItemEvent),
  replies: z.array(OpenItemReply),
});
export type OpenItem = z.infer<typeof OpenItem>;

export const CreateOpenItem = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(10_000).optional(),
  status: OpenItemStatus.optional(),
  /** Default: derived from the anchor (see `topicForAnchor`). */
  topic: OpenItemTopic.optional(),
  anchor: CommentAnchor.nullable().optional(),
  assignee: PersonRef.nullable().optional(),
  dueDate: DateOnly.nullable().optional(),
  /** Members choose the scope (default internal); guests always create in their own link. */
  linkId: z.string().min(1).nullable().optional(),
});
export type CreateOpenItem = z.infer<typeof CreateOpenItem>;
export const UpdateOpenItem = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(10_000).optional(),
  status: OpenItemStatus.optional(),
  topic: OpenItemTopic.optional(),
  anchor: CommentAnchor.nullable().optional(),
  assignee: PersonRef.nullable().optional(),
  dueDate: DateOnly.nullable().optional(),
});
export type UpdateOpenItem = z.infer<typeof UpdateOpenItem>;
export const CreateOpenItemReply = z.object({ body: z.string().min(1).max(10_000) });
/** "Comment → open item": scope and anchor come from the comment; title defaults to its text. */
export const CommentToOpenItem = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  topic: OpenItemTopic.optional(),
  assignee: PersonRef.nullable().optional(),
  dueDate: DateOnly.nullable().optional(),
});

export const NotificationType = z.enum([
  'comment.reply',
  'comment.mention',
  'comment.new',
  'link.extension_requested',
  'link.expiring',
  'openitem.assigned',
  'openitem.status',
  'openitem.reply',
]);
export const Notification = z.object({
  id,
  type: NotificationType,
  payload: z.record(z.string(), z.unknown()),
  readAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type Notification = z.infer<typeof Notification>;
export const MarkRead = z.object({ ids: z.array(id).optional(), all: z.boolean().optional() });

export const AdminStats = z.object({
  users: z.number().int(),
  workspaces: z.number().int(),
  boards: z.number().int(),
  activeLinks: z.number().int(),
  storageBytes: z.number().int(),
});

export { Catalog as CatalogSchema } from '../model/schema.js';

/** Workspace template (decision U16) without its data. */
export const TemplateSummary = z.object({
  id,
  name: z.string(),
  items: z.number().int().nonnegative(),
  channels: z.number().int().nonnegative(),
  createdAt: isoDate,
  createdBy: z.string().nullable(),
});
export type TemplateSummary = z.infer<typeof TemplateSummary>;

export const Template = TemplateSummary.extend({ data: TemplateData });
export type Template = z.infer<typeof Template>;

export const CreateTemplate = z.object({
  name: z.string().trim().min(1).max(200),
  data: TemplateData,
});
export const UpdateTemplate = z.object({ name: z.string().trim().min(1).max(200) });

/** The workspace inventory (decision U15); `revision` increases with every save. */
export const Inventory = z.object({
  revision: z.number().int().nonnegative(),
  entries: z.array(InventoryEntry).max(20_000),
  updatedAt: isoDate.nullable(),
  updatedBy: z.string().nullable(),
});
export type Inventory = z.infer<typeof Inventory>;

/** Saving the inventory: `revision` is the revision the edit is based on (409 if stale). */
export const PutInventory = z.object({
  revision: z.number().int().nonnegative(),
  entries: z.array(InventoryEntry).max(20_000),
});
export type PutInventory = z.infer<typeof PutInventory>;

/** Portable board file (U7): `GET /boards/:id/export` response, `POST …/boards/import` body. */
export { PortableBoard } from '../export/portable.js';
/** `POST /workspaces/:ws/boards/import?name=…` overrides the board name of the package. */
export const ImportBoardQuery = z.object({ name: z.string().trim().min(1).max(200).optional() });

/** AI rider import (decisions U9/U10). */
export const AiImageMime = z.enum(['image/png', 'image/jpeg']);
export const AiUsage = z.object({
  promptTokens: z.number().int().nonnegative().optional(),
  completionTokens: z.number().int().nonnegative().optional(),
  totalTokens: z.number().int().nonnegative().optional(),
  cost: z.number().nonnegative().optional(),
});
export type AiUsage = z.infer<typeof AiUsage>;

const pageText = z.string().max(100_000);
const hint = z.string().max(2000);

/** Stateless extraction: one page = rendered image (base64, no data: prefix) + optional text layer. */
export const AiPageInput = z.object({
  image: z.string().min(1).max(6_000_000),
  mime: AiImageMime.default('image/png'),
  text: pageText.optional(),
});
export const AiExtractRequest = z.object({
  kind: AiImportKind.default('rider'),
  pages: z.array(AiPageInput).min(1).max(50),
  hint: hint.optional(),
});
export const AiExtractResponse = z.object({
  /** Set for `kind: 'rider'`. */
  extraction: AiExtraction.optional(),
  /** Set for `kind: 'materials'`. */
  materials: AiMaterialExtraction.optional(),
  model: z.string(),
  usage: AiUsage.nullable(),
});
export type AiExtractResponse = z.infer<typeof AiExtractResponse>;

export const AI_IMPORT_STATUSES = ['draft', 'extracted', 'applied'] as const;
export const AiImportStatus = z.enum(AI_IMPORT_STATUSES);
export type AiImportStatus = z.infer<typeof AiImportStatus>;

/** A page of an import session; the image is a board file (`GET /files/:fileId`). */
export const AiImportPage = z.object({
  fileId: id,
  text: pageText.optional(),
  /** Original file name and 1-based page (PDF) the image was rendered from. */
  sourceName: z.string().max(255).optional(),
  sourcePage: z.number().int().positive().optional(),
  /** Included when the whole session is (re-)extracted. */
  selected: z.boolean(),
});
export type AiImportPage = z.infer<typeof AiImportPage>;

export const AiImportSessionSummary = z.object({
  id,
  boardId: id,
  kind: AiImportKind,
  title: z.string(),
  status: AiImportStatus,
  /** Wizard step to resume at (1 source … 5 import). */
  step: z.number().int().min(1).max(5),
  model: z.string().nullable(),
  pageCount: z.number().int().nonnegative(),
  createdBy: z.object({ id, name: z.string() }).nullable(),
  createdAt: isoDate,
  updatedAt: isoDate,
  appliedAt: isoDate.nullable(),
});
export type AiImportSessionSummary = z.infer<typeof AiImportSessionSummary>;

export const AiImportSession = AiImportSessionSummary.extend({
  hint: z.string(),
  pages: z.array(AiImportPage),
  /** Raw recognition (merged over page re-runs). */
  extraction: AiExtraction.nullable(),
  /** User-corrected extraction (step 3); the merge uses it when present. */
  edited: AiExtraction.nullable(),
  /** Per-row merge decisions (op key → selected), step 4. */
  decisions: z.record(z.string(), z.boolean()),
  /** Material-list sessions (`kind: 'materials'`, U11): raw and corrected lines. */
  materials: AiMaterialExtraction.nullable(),
  materialsEdited: AiMaterialExtraction.nullable(),
  usage: AiUsage.nullable(),
});
export type AiImportSession = z.infer<typeof AiImportSession>;

export const CreateAiImportSession = z.object({
  kind: AiImportKind.default('rider'),
  title: z.string().trim().max(200).optional(),
  hint: hint.optional(),
  pages: z
    .array(AiImportPage.omit({ selected: true }).extend({ selected: z.boolean().default(true) }))
    .min(1)
    .max(50),
});
export const UpdateAiImportSession = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  hint: hint.optional(),
  step: z.number().int().min(1).max(5).optional(),
  /** Page selection by index. */
  selectedPages: z.array(z.number().int().nonnegative()).optional(),
  edited: AiExtraction.nullable().optional(),
  decisions: z.record(z.string(), z.boolean()).optional(),
  materialsEdited: AiMaterialExtraction.nullable().optional(),
  /** `applied` marks the session as imported (sets `appliedAt`). */
  status: AiImportStatus.optional(),
});
/** Run recognition on all selected pages, or only on the given page indices (re-run). */
export const RunAiExtraction = z.object({
  pages: z.array(z.number().int().nonnegative()).min(1).optional(),
});

// ---------------------------------------------------------------- appointments (U85, OIL-164)
/** Local day of the venue, YYYY-MM-DD. */
export const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Local wall-clock time of the venue, HH:MM. */
export const Clock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const RsvpResponse = z.enum(['pending', 'accepted', 'tentative', 'declined']);
export type RsvpResponse = z.infer<typeof RsvpResponse>;
/** Who is invited: a workspace member, a guest person of the board, or any e-mail address. */
export const InviteeRef = z.union([
  z.object({ userId: id }),
  z.object({ guestPersonId: id }),
  z.object({ email: Email, name: z.string().trim().max(120).optional() }),
]);
export type InviteeRef = z.infer<typeof InviteeRef>;
export const Invitee = z.object({
  id,
  kind: z.enum(['member', 'guest', 'email']),
  /** User id or guest person id (null for plain e-mail addresses). */
  refId: z.string().nullable(),
  name: z.string(),
  /** Only for members of the workspace; guests and the answer page never see addresses. */
  email: z.string().nullable(),
  response: RsvpResponse,
  respondedAt: isoDate.nullable(),
});
export type Invitee = z.infer<typeof Invitee>;
export const MeetingFields = z.object({
  title: z.string().trim().min(1).max(200),
  date: Day,
  start: Clock,
  end: Clock.optional(),
  timezone: z.string().min(1).max(64),
  location: z.string().trim().max(300).optional(),
  url: z.string().trim().url().max(1000).optional(),
  note: z.string().trim().max(5000).optional(),
});
export const SaveMeeting = MeetingFields.extend({ invitees: z.array(InviteeRef).max(100) });
export type SaveMeeting = z.infer<typeof SaveMeeting>;
export const Meeting = z.object({
  id,
  boardId: id,
  title: z.string(),
  date: Day,
  start: Clock,
  end: Clock.nullable(),
  timezone: z.string(),
  location: z.string().nullable(),
  url: z.string().nullable(),
  note: z.string().nullable(),
  organizer: z.string(),
  sequence: z.number().int(),
  cancelled: z.boolean(),
  updatedAt: isoDate,
  invitees: z.array(Invitee),
  /** The viewer's own invitation (answer buttons), null when not invited. */
  mine: z.string().nullable(),
});
export type Meeting = z.infer<typeof Meeting>;
export const Rsvp = z.object({ response: z.enum(['accepted', 'tentative', 'declined']) });
export type Rsvp = z.infer<typeof Rsvp>;
/** People who can be invited to a meeting of a board. */
export const Invitable = z.object({
  kind: z.enum(['member', 'guest']),
  id,
  name: z.string(),
  /** Guests: the link they belong to. */
  linkName: z.string().nullable(),
});
export type Invitable = z.infer<typeof Invitable>;
/** The answer page of a personal invitation link (no login). */
export const InviteView = z.object({
  boardName: z.string(),
  meeting: Meeting,
  /** Board (members) or share link (guests) to open; null when there is none. */
  boardUrl: z.string().nullable(),
});
export type InviteView = z.infer<typeof InviteView>;
/** A person's calendar subscription; `url` only right after it was created. */
export const CalendarFeed = z.object({
  active: z.boolean(),
  createdAt: isoDate.nullable(),
  url: z.string().nullable(),
});
export type CalendarFeed = z.infer<typeof CalendarFeed>;
/**
 * `POST /boards/:id/replace?snapshotName=…` (U93): name of the snapshot stored before the
 * content is replaced (the client sends it in the UI language).
 */
export const ReplaceBoardQuery = z.object({
  snapshotName: z.string().trim().min(1).max(200).optional(),
});
/** Default name of that snapshot. */
export const REPLACE_SNAPSHOT_NAME = 'Before replace from file';
